import { useEffect, useState } from "react";
import { api, formatPrice, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { HeroPhoto } from "@/components/staff/HeroPhoto";
import { OpeningHours } from "@/components/staff/OpeningHours";

const EMPTY = { name_it: "", name_en: "", desc_it: "", desc_en: "", price: "", category: "caffe", image: "", available: true, stamps_eligible: false };

export function MenuManager() {
  const { t } = useLanguage();
  const [menu, setMenu] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cover, setCover] = useState("1.50");
  const [settings, setSettings] = useState(null);

  const load = () => api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setCover(String(r.data.cover_price));
    }).catch(() => {});
  }, []);

  const saveCover = async () => {
    if (!settings) return;
    try {
      const payload = { ...settings, cover_price: parseFloat(cover) || 0 };
      await api.put("/settings", payload);
      setSettings(payload);
      toast.success(t("settings_saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) await api.patch(`/menu/${editing.item_id}`, payload);
      else await api.post("/menu", payload);
      toast.success(t("item_saved"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/menu/${item.item_id}`);
      toast.success(t("item_deleted"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleAvailable = async (item) => {
    await api.patch(`/menu/${item.item_id}`, { ...item, available: !item.available });
    load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const [promoItem, setPromoItem] = useState(null);
  const [promoPrice, setPromoPrice] = useState("");
  const openPromo = (item) => { setPromoItem(item); setPromoPrice(String((item.price * 0.8).toFixed(2))); };
  const savePromo = async () => {
    try {
      await api.put(`/menu/${promoItem.item_id}/promo`, { promo_price: parseFloat(promoPrice) || 0 });
      toast.success(t("promo_saved"));
      setPromoItem(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const clearPromo = async (item) => {
    try {
      await api.delete(`/menu/${item.item_id}/promo`);
      toast.success(t("promo_removed"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="menu-manager">
      <HeroPhoto />
      <OpeningHours />
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-[#E8DFD5] rounded-2xl p-4">
        <Label className="text-xs font-semibold text-[#4A2C2A]">{t("cover_per_person")}</Label>
        <Input
          data-testid="cover-price-input"
          type="number"
          step="0.50"
          min="0"
          value={cover}
          onChange={(e) => setCover(e.target.value)}
          className="w-24 bg-white border-[#E8DFD5]"
        />
        <Button data-testid="save-cover-btn" size="sm" onClick={saveCover} disabled={!settings} className="rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">
          {t("save")}
        </Button>
      </div>
      <Button data-testid="add-menu-item-btn" onClick={openNew} className="rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] gap-1.5 mb-4">
        <Plus className="w-4 h-4" /> {t("add_item")}
      </Button>
      <div className="bg-white border border-[#E8DFD5] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8DFD5] text-left text-xs uppercase tracking-widest text-[#786C66]">
                <th className="p-3">{t("name_it")}</th>
                <th className="p-3">{t("category")}</th>
                <th className="p-3">{t("price")}</th>
                <th className="p-3">{t("available")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {menu.map((item) => (
                <tr key={item.item_id} data-testid={`menu-row-${item.item_id}`} className="border-b border-[#F6F1E9] last:border-0">
                  <td className="p-3 font-semibold text-[#1F1615]">{item.name_it}</td>
                  <td className="p-3 text-[#786C66]">{t(`cat_${item.category}`)}</td>
                  <td className="p-3 text-[#D97706] font-bold">
                    {item.promo_price != null ? (
                      <span data-testid={`promo-active-${item.item_id}`}><span className="line-through text-[#786C66] font-normal mr-1">{formatPrice(item.price)}</span>{formatPrice(item.promo_price)}</span>
                    ) : formatPrice(item.price)}
                  </td>
                  <td className="p-3">
                    <Switch data-testid={`available-switch-${item.item_id}`} checked={item.available} onCheckedChange={() => toggleAvailable(item)} />
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <Button
                        data-testid={`promo-item-${item.item_id}`}
                        variant="ghost"
                        size="icon"
                        title={t("promo_of_day")}
                        onClick={() => (item.promo_price != null ? clearPromo(item) : openPromo(item))}
                      >
                        <Sparkles className={`w-4 h-4 ${item.promo_price != null ? "text-[#D97706] fill-[#D97706]" : "text-[#786C66]"}`} />
                      </Button>
                      <Button data-testid={`edit-item-${item.item_id}`} variant="ghost" size="icon" onClick={() => openEdit(item)}>
                        <Pencil className="w-4 h-4 text-[#786C66]" />
                      </Button>
                      <Button data-testid={`delete-item-${item.item_id}`} variant="ghost" size="icon" onClick={() => remove(item)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FDFBF7] max-w-md max-h-[90vh] overflow-y-auto" data-testid="menu-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#4A2C2A]">{editing ? t("edit_item") : t("add_item")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("name_it")}</Label><Input data-testid="form-name-it" value={form.name_it} onChange={set("name_it")} className="bg-white border-[#E8DFD5]" /></div>
              <div><Label className="text-xs">{t("name_en")}</Label><Input data-testid="form-name-en" value={form.name_en} onChange={set("name_en")} className="bg-white border-[#E8DFD5]" /></div>
            </div>
            <div><Label className="text-xs">{t("desc_it")}</Label><Input data-testid="form-desc-it" value={form.desc_it} onChange={set("desc_it")} className="bg-white border-[#E8DFD5]" /></div>
            <div><Label className="text-xs">{t("desc_en")}</Label><Input data-testid="form-desc-en" value={form.desc_en} onChange={set("desc_en")} className="bg-white border-[#E8DFD5]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("price")}</Label><Input data-testid="form-price" type="number" step="0.10" min="0" value={form.price} onChange={set("price")} className="bg-white border-[#E8DFD5]" /></div>
              <div>
                <Label className="text-xs">{t("category")}</Label>
                <select data-testid="form-category" value={form.category} onChange={set("category")} className="w-full h-9 rounded-md border border-[#E8DFD5] bg-white px-3 text-sm">
                  {["caffe", "dolci", "aperitivi", "bevande"].map((c) => <option key={c} value={c}>{t(`cat_${c}`)}</option>)}
                </select>
              </div>
            </div>
            <div><Label className="text-xs">{t("image_url")}</Label><Input data-testid="form-image" value={form.image} onChange={set("image")} className="bg-white border-[#E8DFD5]" /></div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t("available")}</Label>
              <Switch data-testid="form-available" checked={form.available} onCheckedChange={(v) => setForm((f) => ({ ...f, available: v }))} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t("stamps_eligible")}</Label>
              <Switch data-testid="form-stamps-eligible" checked={form.stamps_eligible} onCheckedChange={(v) => setForm((f) => ({ ...f, stamps_eligible: v }))} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="save-item-btn" onClick={save} className="flex-1 rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">{t("save")}</Button>
              <Button data-testid="cancel-item-btn" variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#E8DFD5]">{t("cancel")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!promoItem} onOpenChange={(o) => !o && setPromoItem(null)}>
        <DialogContent className="bg-[#FDFBF7] max-w-sm" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#4A2C2A]">{t("promo_of_day")}: {promoItem?.name_it}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#786C66]">{t("promo_hint")} {promoItem && formatPrice(promoItem.price)}</p>
          <Label className="text-xs">{t("promo_price")}</Label>
          <Input data-testid="promo-price-input" type="number" step="0.10" min="0" value={promoPrice} onChange={(e) => setPromoPrice(e.target.value)} className="bg-white border-[#E8DFD5]" />
          <div className="flex gap-2 pt-2">
            <Button data-testid="save-promo-btn" onClick={savePromo} className="flex-1 rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">{t("save")}</Button>
            <Button variant="outline" onClick={() => setPromoItem(null)} className="rounded-full border-[#E8DFD5]">{t("cancel")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
